import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowSquareOut, DeviceMobile, DeviceTablet } from '@phosphor-icons/react';
import { config } from '@/config';

type Device = 'phone' | 'tablet';

// Logical viewport of each bezel, in CSS px, INCLUDING the bezel's own
// padding (the screen — and therefore the embedded PWA's viewport — is
// this minus the bezel padding defined in global.css). Phone resolves to
// a 390px screen, tablet to 820px: real device widths, so the PWA always
// renders the same layout a member sees on hardware. The whole bezel is
// then visually scaled to fit the available width (see the ResizeObserver
// below) rather than reflowed narrower — a faithful preview, never a
// squished one.
//
// `minW` is the smallest on-screen bezel width we ever shrink to on an
// unusually narrow card — a floor so the bezel can't collapse to nothing.
const DEVICE_DIMS: Record<Device, { w: number; h: number; minW: number }> = {
  phone: { w: 414, h: 897, minW: 180 },
  tablet: { w: 856, h: 1113, minW: 300 },
};

// The bezel renders at half its hardware pixels by default. Full size looked
// massive on a wide desktop card; half keeps a phone/tablet that reads as a
// device without dominating the page. The page scrolls to reveal its height.
const MAX_SCALE = 0.5;

// Settings → App: embeds the universal app (app.sportadmin.io) inside an
// iPhone or iPad-shaped bezel so admins can drive the member + leader
// experience without leaving the admin tab. The cross-origin embed
// works because (a) the admin's CSP allows frame-src https://*.sportadmin.io
// and (b) the API binary that serves app.sportadmin.io relaxes
// frame-ancestors to allow the SPA origin (see cmd/api/main.go's
// securityHeadersMiddleware app-host branch). Auth is shared via the
// apex `.sportadmin.io` session cookie set by SessionCookieBridge, so
// the iframe lands logged in instead of bouncing through Zitadel.
//
// The "Open in new tab" button is the escape hatch for anything iframe
// doesn't render well (push prompts, biometric unlock, deep-link to a
// share sheet) — it just opens app.sportadmin.io in a new tab with
// rel="noopener" so the popup can't navigate us.
export function App() {
  const { t } = useTranslation('common');
  const [device, setDevice] = useState<Device>('phone');

  const appURL = config.appURL;
  const hasURL = appURL.length > 0;

  // Size the bezel to the frame's WIDTH only — never to the viewport height.
  // We deliberately let the device render tall and let the PAGE scroll to
  // reveal the rest, rather than shrinking the whole device to fit a short
  // laptop viewport (which turned it into a useless sliver). CSS can't derive
  // this: transform: scale() needs a unitless number and calc() can't divide
  // one length by another. We cap the scale at MAX_SCALE (half hardware size)
  // and floor it at the per-device `minW` for very narrow cards.
  const frameRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const { w: deviceW, h: deviceH, minW: deviceMinW } = DEVICE_DIMS[device];

  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el) return;

    const recompute = () => {
      const styles = getComputedStyle(el);
      const padX = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
      // Horizontal room = the frame's padding-excluded content box.
      const availW = el.clientWidth - padX;
      const minScale = deviceMinW / deviceW;
      setScale(Math.max(minScale, Math.min(MAX_SCALE, availW / deviceW)));
    };

    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    window.addEventListener('resize', recompute);
    recompute();
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', recompute);
    };
  }, [deviceW, deviceH, deviceMinW]);

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-4 md:p-8">
      <header className="flex flex-col gap-1">
        <p className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('settings.title')}
        </p>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight md:text-4xl">
          {t('settings.app.title')}
        </h1>
        <p className="text-muted-foreground">{t('settings.app.subtitle')}</p>
      </header>

      <div className="flex flex-col gap-6 rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div
            className="design-drawer__devices"
            role="tablist"
            aria-label={t('settings.app.deviceLabel')}
          >
            <button
              type="button"
              role="tab"
              aria-selected={device === 'phone'}
              className={`design-drawer__device${device === 'phone' ? ' design-drawer__device--active' : ''}`}
              onClick={() => setDevice('phone')}
            >
              <DeviceMobile className="h-4 w-4" weight="bold" aria-hidden />
              <span>{t('settings.app.devicePhone')}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={device === 'tablet'}
              className={`design-drawer__device${device === 'tablet' ? ' design-drawer__device--active' : ''}`}
              onClick={() => setDevice('tablet')}
            >
              <DeviceTablet className="h-4 w-4" weight="bold" aria-hidden />
              <span>{t('settings.app.deviceTablet')}</span>
            </button>
          </div>

          {hasURL && (
            <a
              href={appURL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-pill bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-transform duration-200 hover:scale-[1.04]"
            >
              <span>{t('settings.app.openInNewTab')}</span>
              <ArrowSquareOut className="h-4 w-4" weight="bold" aria-hidden />
            </a>
          )}
        </div>

        <div
          ref={frameRef}
          className={`app-preview__frame app-preview__frame--${device}`}
          style={{ '--device-scale': scale } as CSSProperties}
        >
          {hasURL ? (
            // The sizer occupies the SCALED footprint in normal flow; the
            // shell is absolutely positioned inside it and scaled from the
            // top-left so its visual box exactly fills the sizer.
            <div className="app-preview__sizer">
              <div className="app-preview__shell">
                <iframe
                  src={appURL}
                  title={t('settings.app.iframeTitle')}
                  className="app-preview__screen"
                  // allow camera/mic/notifications/etc the app might ask for —
                  // these still require user gesture inside the iframe, the
                  // attribute just tells the browser they're permitted.
                  allow="camera; microphone; geolocation; clipboard-write"
                />
              </div>
            </div>
          ) : (
            <p className="app-preview__placeholder">
              {t('settings.app.previewUnavailable')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
